/**
 * Target clients section – Researchers, Innovators, Industries, SMEs, Institutions
 * Placed just under the hero with card layout and placeholder images.
 */
import { motion } from 'motion/react';
import { FlaskConical, Lightbulb, Factory, Building2, GraduationCap } from 'lucide-react';
import { content } from '@/landing/data/content';

const iconMap = {
  FlaskConical,
  Lightbulb,
  Factory,
  Building2,
  GraduationCap,
};

// Relevant Unsplash images per audience (Researchers, Innovators, Industries, SMEs, Institutions)
const cardImages = [
  'https://images.unsplash.com/photo-1443890484047-5eaa67d1d630?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800', // Researchers – lab / science
  'https://images.unsplash.com/photo-1637856794303-d864ce316444?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800', // Innovators – collaboration / ideas
  'https://images.unsplash.com/photo-1685302863485-e1a2af4a637f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800', // Industries – factory / manufacturing
  'https://images.unsplash.com/photo-1670851050245-d861fd433d06?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800', // SMEs – small business / office
  'https://images.unsplash.com/photo-1740065592671-9cb593ee9b78?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800', // Institutions – graduation / campus
];

export function TargetClients() {
  return (
    <section id="target-clients" className="relative py-20 lg:py-28 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-[var(--gold)] mb-3">
            Who we serve
          </h2>
          <p className="text-lg text-[var(--text-muted)] max-w-2xl mx-auto font-light">
            Researchers, innovators, industries, SMEs, and institutions
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          {content.targetClients.map((client, index) => {
            const Icon = iconMap[client.icon as keyof typeof iconMap];
            const imgSrc = cardImages[index] ?? cardImages[0];
            return (
              <motion.article
                key={client.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="group flex flex-col rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,168,76,0.3)] transition-all duration-300 h-full"
              >
                <div className="relative h-44 flex-shrink-0 overflow-hidden">
                  <img
                    src={imgSrc}
                    alt={client.title}
                    className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/80 to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-lg bg-[var(--navy)]/90 px-3 py-1.5 backdrop-blur-sm border border-[rgba(201,168,76,0.2)]">
                    {Icon && <Icon className="w-5 h-5 text-[var(--gold)]" />}
                    <span className="font-semibold text-white">{client.title}</span>
                  </div>
                </div>
                <div className="insolip-card-gradient flex-1 flex flex-col justify-center p-5 rounded-b-2xl min-h-[100px]">
                  <p className="text-white/90 text-sm leading-relaxed font-light">
                    {client.description}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
